/* ============================================================
   Runtime <head> metadata for multi-tenant deployment.

   public/index.html ships with build-time placeholders
   (%REACT_APP_SITE_TITLE%, %REACT_APP_META_DESCRIPTION%,
   %REACT_APP_META_THEME_COLOR%, %REACT_APP_FAVICON%,
   %REACT_APP_APP_LOGO%). One build serves many tenants, so those env
   vars are often unset and CRA leaves the literal "%...%" text in the
   page. This helper overwrites every one of those tags at runtime.

   Priority for each value:
     1. Remote per-tenant config (GET v1/config)          [runtime]
     2. Build-time env var (REACT_APP_*)                  [per-build]
     3. Tenant logo from tenant-info (icons only)         [runtime]
     4. Safe default                                      [fallback]

   Note: the public v1/config payload currently exposes accent_color but
   no app name / description / logo. The name/description/logo lookups
   below read the optional legacy `App_setting[0]` shape (already used by
   MenuItems/Login for app_logo) and a few flat aliases, so they light up
   automatically if the backend starts returning them.
   ============================================================ */

import { resolveRemoteBrandColor, isValidCssColor, DEFAULT_BRAND } from "./applyTheme";

const DEFAULT_DESCRIPTION = "Watch movies, series and live TV.";

/** A value is usable if it's a non-empty string that isn't an unreplaced %PLACEHOLDER%. */
export function isUsable(value) {
  if (typeof value !== "string") return false;
  const v = value.trim();
  if (!v) return false;
  return !/%[A-Z0-9_]+%/.test(v);
}

function firstUsable(...values) {
  for (const v of values) if (isUsable(v)) return v.trim();
  return null;
}

function publicUrl() {
  const p = process.env.PUBLIC_URL;
  return isUsable(p) ? p.replace(/\/$/, "") : "";
}

/** Absolute / root-relative / data URLs pass through; bare file names are served from /public. */
function toAssetUrl(value) {
  if (/^(https?:)?\/\//i.test(value) || /^(data|blob):/i.test(value) || value.startsWith("/")) {
    return value;
  }
  return `${publicUrl()}/${value}`;
}

function setting(config) {
  const s = config?.App_setting;
  return (Array.isArray(s) ? s[0] : s) || {};
}

/** Work out every head value from config → env → defaults. Pure (apart from reading env/location). */
export function resolveDocumentMeta(config, tenantLogo) {
  const app = setting(config);
  const env = process.env;

  const hostname =
    (typeof window !== "undefined" && window.location && window.location.hostname) || "";

  const title =
    firstUsable(
      config?.site_title,
      config?.app_name,
      config?.platform_name,
      app.site_title,
      app.app_name,
      app.name,
      env.REACT_APP_SITE_TITLE
    ) || hostname || "TVID";

  const description =
    firstUsable(
      config?.meta_description,
      config?.description,
      app.meta_description,
      app.description,
      env.REACT_APP_META_DESCRIPTION
    ) || DEFAULT_DESCRIPTION;

  // Same brand-colour priority as applyTheme (config → REACT_APP_THEME_COLOR → copper);
  // REACT_APP_META_THEME_COLOR still wins over the generic theme env var.
  // Invalid values (e.g. a CMS preset label like "midnight") are skipped.
  const themeColor =
    [resolveRemoteBrandColor(config), env.REACT_APP_META_THEME_COLOR, env.REACT_APP_THEME_COLOR]
      .filter(isUsable)
      .map((c) => c.trim())
      .find(isValidCssColor) || DEFAULT_BRAND;

  const remoteLogo = firstUsable(config?.app_logo, app.app_logo, config?.logo, app.logo);
  const remoteFavicon = firstUsable(config?.favicon, app.favicon, app.app_favicon);

  // An explicit build-time env icon wins over the tenant-info logo (a wide header
  // wordmark is unreadable at 16x16); the logo only replaces the placeholder favicon.ico.
  const faviconSrc = firstUsable(remoteFavicon, remoteLogo, env.REACT_APP_FAVICON, tenantLogo);
  const favicon = faviconSrc ? toAssetUrl(faviconSrc) : `${publicUrl()}/favicon.ico`;

  const touchSrc = firstUsable(remoteLogo, env.REACT_APP_APP_LOGO, tenantLogo, remoteFavicon);
  const appleTouchIcon = touchSrc ? toAssetUrl(touchSrc) : `${publicUrl()}/logo192.png`;

  return { title, description, themeColor, favicon, appleTouchIcon };
}

function upsertHeadTag(tag, selector, attrs) {
  const doc = document;
  let el = doc.head.querySelector(selector);
  if (!el) {
    el = doc.createElement(tag);
    doc.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

/**
 * Browsers (Chrome/Safari) don't reliably repaint the tab icon when an existing
 * <link rel="icon"> just has its href changed, so swap in a fresh node instead.
 * Removes every link matching `selector` (e.g. "shortcut icon" variants too), so
 * no stale icon survives. No-op when the single existing link already matches.
 */
function replaceIconLink(selector, rel, href) {
  const head = document.head;
  const existing = Array.from(head.querySelectorAll(selector));
  if (existing.length === 1 && existing[0].getAttribute("href") === href) return existing[0];

  const el = document.createElement("link");
  el.setAttribute("rel", rel);
  el.setAttribute("href", href);
  if (existing.length) head.insertBefore(el, existing[0]);
  else head.appendChild(el);
  existing.forEach((node) => node.remove());
  return el;
}

/**
 * Write title, description, theme-color, favicon and apple-touch-icon to <head>.
 * Safe to call repeatedly (with null at startup, then with the config payload,
 * or `{}` if /v1/config failed). `tenantLogo` is the tenant-info logoUrl.
 */
export function applyDocumentMeta(config, tenantLogo) {
  if (typeof document === "undefined") return null;
  const meta = resolveDocumentMeta(config, tenantLogo);

  document.title = meta.title;
  upsertHeadTag("meta", 'meta[name="description"]', { name: "description", content: meta.description });
  upsertHeadTag("meta", 'meta[name="theme-color"]', { name: "theme-color", content: meta.themeColor });
  // rel~="icon" matches "icon" and "shortcut icon" (but not "apple-touch-icon").
  replaceIconLink('link[rel~="icon"]', "icon", meta.favicon);
  replaceIconLink('link[rel="apple-touch-icon"]', "apple-touch-icon", meta.appleTouchIcon);

  return meta;
}

export { DEFAULT_DESCRIPTION };
